import { BadgeCheck, MapPin, Sparkles, Zap } from "lucide-react";
import type { DiscoveryCandidate } from "@/lib/discovery/types";
import BgcStatusBadge from "@/components/safety/BgcStatusBadge";
import SignedImage from "@/components/media/SignedImage";

interface Props {
  candidate: DiscoveryCandidate;
}

const CandidateCard = ({ candidate: c }: Props) => {
  const cover = c.photos?.[0];
  return (
    <article className="relative w-full overflow-hidden rounded-3xl border border-border bg-card shadow-elevated">
      <div className="relative aspect-[3/4] w-full bg-muted">
        {cover ? (
          <SignedImage
            src={cover}
            alt={c.display_name ?? "Member"}
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No photo yet
          </div>
        )}

        {/* rank badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {c.is_amplified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
              <Zap className="h-3 w-3" /> Amplified
            </span>
          )}
          {c.is_priority && !c.is_amplified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-secondary-foreground">
              <Sparkles className="h-3 w-3" /> Priority
            </span>
          )}
        </div>

        {/* gradient overlay + info */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-5 text-white">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-2xl font-semibold">
              {c.display_name ?? "Member"}
            </h3>
            {c.community_verified && (
              <BadgeCheck className="h-5 w-5 text-primary" aria-label="Community verified" />
            )}
            <BgcStatusBadge
              status={c.bgc_verified ? "verified" : "none"}
              publicOnly
            />
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-white/80">
            {c.pronouns && <span>{c.pronouns}</span>}
            {c.distance_km !== null && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {Math.round(c.distance_km)} km
              </span>
            )}
          </div>
        </div>
      </div>

      {c.prompts?.length ? (
        <div className="space-y-3 p-5">
          {c.prompts.slice(0, 2).map((p) => (
            <div key={p.key}>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {p.text}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-foreground">
                {p.answer}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
};

export default CandidateCard;
